import { FC, memo, useCallback, useMemo, useRef, useState } from "react";
import { Section } from "./Section";
import { AdminsList } from "./AdminsList";
import { CreateTenantAdminBottomSheet, type CreateTenantAdminData } from "./CreateTenantAdminBottomSheet";
import { CreateTenantAdminForm } from "./CreateTenantAdminForm";
import { PressableScale } from "pressto";
import { View } from "@src/components";
import { Text } from "@src/components";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import type { BottomSheetModal } from "@gorhom/bottom-sheet";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { useAccessManagement, useInviteUser } from "@visioni/a3s-access/app";
import type { ListItem } from "./List";
import { ActivityIndicator, ViewProps } from "react-native";
import { useRouter } from "expo-router";
import { isWeb } from "@src/utils/web-helpers";
// @ts-expect-error baseline 2026-02-16
import { RightDrawer } from "@src/components/web";

type AdminsSectionProps = {
  title: string;
  style?: ViewProps["style"];
  subtitle?: string;
};

export const AdminsSection: FC<AdminsSectionProps> = memo(({ title, style, subtitle }) => {
  const { theme } = useUnistyles();
  const createAdminRef = useRef<BottomSheetModal>(null);
  const router = useRouter();

  // Web-only: RightDrawer state (on native the bottom sheet is used instead).
  const [isCreateAdminDrawerVisible, setIsCreateAdminDrawerVisible] = useState(false);
  const handleCloseCreateAdminDrawer = useCallback(() => {
    setIsCreateAdminDrawerVisible(false);
  }, []);

  const { data, isLoading } = useAccessManagement();
  const inviteUser = useInviteUser();

  const adminItems: ListItem[] = useMemo(() => {
    if (!data) return [];
    const adminUserIds = new Set(data.roles.filter((r) => r.role === "tenant_admin").map((r) => String(r.user)));
    return data.users
      .filter((u) => adminUserIds.has(String(u.id)))
      .map((u) => ({
        id: String(u.id),
        name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
        email: u.email,
        phoneNumber: u.phoneNumber,
      }));
  }, [data]);

  const handleCreateNewAdmin = useCallback(() => {
    if (isWeb) {
      setIsCreateAdminDrawerVisible(true);
      return;
    }
    createAdminRef.current?.present();
  }, []);

  const handleAdminPress = useCallback(
    (userId: string) => {
      router.push(`/settings/site-access/camera-access/${encodeURIComponent(userId)}`);
    },
    [router],
  );

  const handleSaveNewAdmin = useCallback(
    (formData: CreateTenantAdminData) => {
      inviteUser.mutate(
        { email: formData.email, role: "tenant_admin" },
        {
          onSuccess: () => showSuccessToast({ message: "Organisation admin created" }),
          onError: (error) =>
            showErrorToast({
              message:
                error.message.includes("does not have an existing account") ?
                  "This user doesn't have an account yet. Please contact support to create one."
                : "Failed to create organisation admin",
            }),
        },
      );
    },
    [inviteUser],
  );

  const handleWebSaveNewAdmin = useCallback(
    (formData: CreateTenantAdminData) => {
      handleSaveNewAdmin(formData);
      setIsCreateAdminDrawerVisible(false);
    },
    [handleSaveNewAdmin],
  );

  if (isWeb) {
    return (
      <Section title={title} style={style} subtitle={subtitle}>
        {isLoading ?
          <ActivityIndicator style={styles.loader} />
        : <AdminsList items={adminItems} onItemPress={handleAdminPress} />}
        <PressableScale onPress={handleCreateNewAdmin}>
          <View style={styles.button}>
            <Text textStyle="sm/regular">Create new organisation admin</Text>
            <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
          </View>
        </PressableScale>
        <RightDrawer
          width={450}
          visible={isCreateAdminDrawerVisible}
          onClose={handleCloseCreateAdminDrawer}
          title="Create new organisation admin"
        >
          <CreateTenantAdminForm onSave={handleWebSaveNewAdmin} />
        </RightDrawer>
      </Section>
    );
  }

  return (
    <Section title={title} style={style} subtitle={subtitle}>
      {isLoading ?
        <ActivityIndicator style={styles.loader} />
      : <AdminsList items={adminItems} onItemPress={handleAdminPress} />}
      <PressableScale onPress={handleCreateNewAdmin}>
        <View style={styles.button}>
          <Text textStyle="sm/regular">Create new organisation admin</Text>
          <Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.iconPrimary} />
        </View>
      </PressableScale>
      <CreateTenantAdminBottomSheet bottomSheetRef={createAdminRef} onSave={handleSaveNewAdmin} />
    </Section>
  );
});

const styles = StyleSheet.create((theme) => ({
  button: {
    height: 44,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: theme.spacing.xl,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.interactive.secondary.default_border,
    backgroundColor: theme.colors.interactive.secondary.background_secondary,
    marginTop: theme.spacing.lg,
  },
  loader: {
    paddingVertical: theme.spacing["5xl"],
  },
}));
